<template>
  <component
    :is="tag"
    ref="root"
    :to="to"
    class="ak-text-link"
    v-bind="attrs"
    @focus="focus"
    @click="clicked"
  >
    <slot />
  </component>
</template>

<script>
import { defineComponent, ref } from 'vue'
import { types } from './TextLink.config'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-text-link>Link</ak-text-link>
   */

  name: 'AkTextLink',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @prop {Function} validator - Ensures a valid type is used
     * @prop {String} type - The type of the property
     * @prop {String} default - The default value of the property
     * @description Controls the element type of the button component that
     * will be rendered
     */
    tag: {
      validator: (v) => types.indexOf(v) >= 0,
      type: String,
      default: 'a',
    },

    /**
     * @type {String|Object}
     * @description The to prop required for router-link components
     */
    to: {
      type: [String, Object],
      default: undefined,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['click'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @param {Object} options.attrs - The attributes method for the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { attrs, emit }) {
    const root = ref(null)

    /**
     * @description Allows parent components to call the focus method
     * on the button element
     */
    const focus = () => {
      try {
        root.value.$el.focus()
      } catch (e) {
        /*istanbul ignore next */
      }
    }

    const clicked = (e) => {
      /* istanbul ignore if */
      if (e && e.originalEvent instanceof Event) {
        e.preventDefault()
        e.stopPropagation()
      }
      emit('click', e)
    }

    return {
      focus,
      attrs,
      clicked,
      root,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-text-link {
  text-decoration: none;
  border-bottom: 1px dotted currentColor;
  cursor: pointer;

  &:hover {
    color: $color-primary;
  }
}
</style>
